import {
  Container,
  Content,
  HeaderContainer,
  LoadingMessage,
  SearchContainer,
  SearchInput,
  Table,
  TableContainer,
} from '@/components/CommonStyles';
import { useQuery } from '@tanstack/react-query';
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  getSortedRowModel,
  SortingState,
  getFilteredRowModel,
  ColumnDef,
  Header,
  HeaderGroup,
  Cell,
} from '@tanstack/react-table';
import { useMemo, useState } from 'react';

interface CommonUserListContainerProps<T> {
  HeaderComponent: React.ComponentType;
  queryKey: string;
  queryFn: () => Promise<any>;
  columns: () => ColumnDef<T, any>[];
}

function CommonUserListContainer<T>({
  HeaderComponent,
  queryKey,
  queryFn,
  columns: getColumns,
}: CommonUserListContainerProps<T>) {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [searchTerm, setSearchTerm] = useState('');

  const { data: dataAPI, isLoading: isLoadingAPI } = useQuery({
    queryKey: [queryKey],
    queryFn: queryFn,
    staleTime: 1000 * 60 * 5,
    refetchOnMount: 'always',
    enabled: true,
  });

  const columns = useMemo(() => {
    return getColumns();
  }, [getColumns]);

  const rows = useMemo(() => {
    const payload = dataAPI?.data;
    const data =
      payload?.data?.data ??
      payload?.data ??
      payload?.result ??
      payload ??
      [];
    return Array.isArray(data) ? data : [];
  }, [dataAPI?.data]);

  const table = useReactTable({
    data: rows,
    columns,
    state: {
      sorting,
      globalFilter: searchTerm,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onGlobalFilterChange: setSearchTerm,
    globalFilterFn: (row, columnId, filterValue) => {
      const value = String(row.getValue(columnId) || '').toLowerCase();
      return value.includes(String(filterValue).toLowerCase());
    },
  });

  return (
    <Container>
      <HeaderComponent />
      <Content>
        {isLoadingAPI ? (
          <LoadingMessage>데이터를 불러오는 중입니다...</LoadingMessage>
        ) : rows.length === 0 ? (
          <LoadingMessage>
            데이터가 없습니다. 잠시 후 다시 시도해주세요.
          </LoadingMessage>
        ) : (
          <>
            <HeaderContainer>
              <SearchContainer>
                <SearchInput
                  type="text"
                  placeholder="검색어를 입력하세요..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </SearchContainer>
            </HeaderContainer>
            <TableContainer>
              <Table>
                <thead>
                  {table
                    .getHeaderGroups()
                    .map((headerGroup: HeaderGroup<T>) => (
                      <tr key={headerGroup.id}>
                        {headerGroup.headers.map(
                          (header: Header<T, unknown>) => (
                            <th
                              key={header.id}
                              onClick={header.column.getToggleSortingHandler()}
                              style={{ cursor: 'pointer' }}
                            >
                              {flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                              {{
                                asc: ' 🔼',
                                desc: ' 🔽',
                              }[header.column.getIsSorted() as string] ?? null}
                            </th>
                          )
                        )}
                      </tr>
                    ))}
                </thead>
                <tbody>
                  {table.getRowModel().rows.map((row) => (
                    <tr key={row.id}>
                      {row.getVisibleCells().map((cell: Cell<T, unknown>) => (
                        <td key={cell.id}>
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </Table>
            </TableContainer>
          </>
        )}
      </Content>
    </Container>
  );
}

export default CommonUserListContainer;
